import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useModal } from 'mui-modal-provider';
import * as React from 'react';
import { useDeleteContactField } from '../../../sic-lib/api/hooks/useContact';
import { FieldValidationType } from '../../../sic-lib/enums/FieldValidationType';
import { Contact } from '../../../sic-lib/types/Contact';
import { ContactAddress, ContactField } from '../../../sic-lib/types/ContactField';
import { stringifyContactAddress } from '../../../sic-lib/utils/contactFieldHelpers';
import { hasMatchingValidationType } from '../../../sic-lib/utils/functionalFilters';
import DialogConfirmAction from '../../Shared/DialogConfirmAction/DialogConfirmAction';
import DisplayDateTime from '../../Shared/DisplayDateTime/DisplayDateTime';
import DisplayPhoneNumber from '../../Shared/DisplayPhoneNumber/DisplayPhoneNumber';
import ContactFieldIconContainer from './ContactFieldIconContainer';
import MenuContactFieldActions from './MenuContactFieldActions/MenuContactFieldActions';

interface Props {
    contact: Contact;
    contactField: ContactField;
    onToggleHandler: () => void;
}

export default function ContactFieldDisplay({ contact, contactField, onToggleHandler }: Props) {
    const { showModal } = useModal();
    const { mutate: deleteContactField } = useDeleteContactField(contact.contactId);

    const [fieldValue, setFieldValue] = React.useState<string>('');

    React.useEffect(() => {
        let value: string =
            contactField.validationType === FieldValidationType.address
                ? stringifyContactAddress(contactField.value as ContactAddress)
                : (contactField.value as string);

        setFieldValue(value);
    }, [contactField.validationType, contactField.value]);

    const handleShowConfirm = () => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Delete Contact Field',
            description: 'Are you sure you want to delete this contact field?',
            onConfirm: async () => deleteContactField({ fieldId: contactField.fieldId }),
            onCancel: () => {
                modal.hide();
            },
        });
    };

    return (
        <Stack direction="row" justifyContent="space-between" spacing={1}>
            <ContactFieldIconContainer contactId={contact.contactId} contactField={contactField} />
            <Stack width="100%">
                <Typography component="span" variant="caption">
                    {contactField.label}
                </Typography>
                {hasMatchingValidationType(FieldValidationType.date)(contactField) && (
                    <DisplayDateTime dateText={contactField.value as string} />
                )}
                {hasMatchingValidationType(FieldValidationType.usMobilePhone)(contactField) && (
                    <DisplayPhoneNumber phoneNumber={contactField.value as string} />
                )}
                {!hasMatchingValidationType([
                    FieldValidationType.date,
                    FieldValidationType.usMobilePhone,
                ])(contactField) && (
                    <Typography
                        component="p"
                        variant="body1"
                        style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}
                    >
                        {fieldValue}
                    </Typography>
                )}
            </Stack>
            <MenuContactFieldActions
                contact={contact}
                fieldId={contactField.fieldId}
                fieldValue={contactField.value}
                fieldValidationType={contactField.validationType}
                fieldIsPrimary={contactField.isPrimary}
                fieldIsReadOnly={contactField.isReadOnly}
                onToggleHandler={onToggleHandler}
                onDeleteHandler={handleShowConfirm}
            />
        </Stack>
    );
}
